import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useI18n } from "../../lib/i18n";
import { analytics } from "../../lib/analytics";
import SectionBoundary from "../layout/SectionBoundary";
import AdfImage from "./AdfImage";
import ProductCard, { explodeColourCards } from "./ProductCard";

/**
 * A horizontal rail of products.
 *
 * Lifted out of the homepage, where it was defined inline and therefore
 * unusable anywhere else — the one component on that page most worth reusing.
 * Items may be normalised products or editorial tiles; the card dispatches on
 * which it is, so a rail can mix a real catalogue row with a campaign tile.
 */
function ProductRail({ eyebrow, title, copy, cta, to, items, testId, count = 4, layout = "rail" }) {
  const isGrid = layout === "grid";
  const { locale } = useI18n();
  const track = useRef(null);
  const [edges, setEdges] = useState({ start: true, end: false });

  // The arrow used to be a Link straight out of the page, and the track a fixed
  // four-column grid with no overflow — so there was nothing to scroll and
  // pressing the arrow left the home page entirely. It scrolls the rail now.
  const measure = useCallback(() => {
    const node = track.current;
    if (!node) return;
    setEdges({
      start: node.scrollLeft <= 2,
      end: node.scrollLeft + node.clientWidth >= node.scrollWidth - 2,
    });
  }, []);

  useEffect(() => {
    measure();
    const node = track.current;
    if (!node) return undefined;
    node.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    return () => {
      node.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, [measure, items]);

  const nudge = (direction) => {
    const node = track.current;
    if (!node) return;
    const card = node.querySelector(".adf-home2-card, .adf-product-card");
    const step = card ? card.getBoundingClientRect().width + 24 : node.clientWidth * 0.8;
    node.scrollBy({ left: direction * step, behavior: "smooth" });
  };

  // Impressions for the rail, so a homepage row can be compared against the
  // catalogue grid rather than being invisible in the funnel.
  const railKey = items.map((item) => item.to).join(",");
  useEffect(() => {
    const products = items.map((item) => item.product).filter(Boolean);
    if (products.length) analytics.viewItemList(testId, products);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [railKey, testId]);

  return (
    <SectionBoundary name={testId}>
    <section className="py-10 md:py-14 bg-white" data-testid={testId}>
      <div className="container mx-auto px-6 md:px-12">
        <div className="flex justify-between items-end mb-10 md:mb-12">
          <div>
            <h2 className="text-2xl md:text-3xl font-serif text-charcoal tracking-tight">{title}</h2>
            {(eyebrow || copy) && (
              <p className="text-charcoal/60 text-sm mt-2 font-medium">{copy || eyebrow}</p>
            )}
          </div>
          {to && cta && (
            <Link
              to={to}
              className="hidden md:block text-xs uppercase tracking-[0.1em] font-medium border-b border-charcoal/30 pb-0.5 hover:border-charcoal transition-colors text-charcoal"
            >
              {cta}
            </Link>
          )}
        </div>

        {/* Two shapes from one component: the default "rail" is four across in
            one row (mobile scrolls, desktop grid); "grid" is a static block that
            wraps `count` items into a 2-up / 4-up grid — e.g. 12 newest as 3×4. */}
        <div
          ref={track}
          data-testid={`${testId}-track`}
          className={isGrid
            ? "grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6"
            : "flex overflow-x-auto snap-x snap-mandatory md:grid md:grid-cols-4 gap-4 md:gap-6 pb-8 md:pb-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] -mx-6 px-6 md:mx-0 md:px-0"}
        >
          {items.flatMap((item) => (
            item.product
              ? explodeColourCards([item.product]).map((p) => ({ ...item, product: p, __cardKey: p.__cardKey }))
              : [item]
          )).slice(0, count).map((item, index) => (
            <div
              key={item.__cardKey || `${item.to}-${item.title}-${index}`}
              className={isGrid ? "w-auto" : "w-[75vw] sm:w-[50vw] md:w-auto shrink-0 snap-start"}
            >
              <RailCard item={item} index={index} listName={testId} />
            </div>
          ))}
        </div>

        <div className="mt-8 md:hidden text-center">
          {to && cta && (
            <Link
              to={to}
              className="text-xs uppercase tracking-[0.1em] font-medium border-b border-charcoal/30 pb-0.5 hover:border-charcoal transition-colors text-charcoal inline-block"
            >
              {cta}
            </Link>
          )}
        </div>

        <div className="hidden">
          <button type="button" onClick={() => nudge(-1)} disabled={edges.start} data-testid={`${testId}-prev`} aria-label={locale === "az" ? "Geri sürüşdür" : "Scroll back"}>
            <ChevronRight size={24} aria-hidden="true" />
          </button>
          <button type="button" onClick={() => nudge(1)} disabled={edges.end} data-testid={`${testId}-next`} aria-label={locale === "az" ? "İrəli sürüşdür" : "Scroll forward"}>
            <ChevronRight size={24} aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
    </SectionBoundary>
  );
}

function RailCard({ item, index, listName }) {
  // Editorial fallback cards have no product row behind them; those keep the
  // simple tile below.
  if (item.product) {
    // The rail shows roughly one and a third cards on a phone — about 75vw, not
    // the 50vw the catalogue grid assumes. Left at the default, the browser
    // picked a source for a 195px slot and painted it into a 293px one.
    return (
      <ProductCard
        product={item.product}
        index={index}
        listName={listName}
        imageSizes="(min-width: 1024px) 25vw, (min-width: 640px) 33vw, 75vw"
        testId="home-product"
      />
    );
  }
  return <EditorialRailCard item={item} index={index} />;
}

function EditorialRailCard({ item, index }) {
  return (
    <article className="adf-home2-card">
      <Link to={item.to} className="adf-home2-card__media">
        <AdfImage
          source={item.image}
          alt={item.title}
          sizes="(min-width: 1100px) 220px, (min-width: 700px) 25vw, 48vw"
          className="adf-home2-card__image"
          priority={index < 2}
        />
        {item.isNew && <span className="adf-home2-pill">Yeni</span>}
      </Link>
      <div className="adf-home2-card__body">
        {item.meta && <p>{item.meta}</p>}
        <Link to={item.to}>{item.title}</Link>
        {item.price && <span>{item.price}</span>}
      </div>
    </article>
  );
}

export default ProductRail;
